import moment from 'moment-timezone';
import React from 'react';
import Text from 'components/Text';
import Row from 'components/Row';
import Button from 'components/Button';
import Columns2 from 'components/Columns2';
import MediaObject from 'components/MediaObject';
import {
  CoachingSessionState,
  ParticipantMetadata,
  CoachingSession,
} from 'coaching/CoachingSessionInfoContainer';
import CoachingHomeFreeTrialCard from 'coaching/CoachingHomeFreeTrialCard';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';

import 'components/styles/CoachingSessionInfoContainer.scss';

export type Props = {
  joinSessionButton?: React.ReactElement;
  messageCoachButton?: React.ReactElement;
  userUuid?: string;
  participantMetadata: ParticipantMetadata;
  coachingSession?: CoachingSession;
  coachingSessionState: CoachingSessionState;
};

export default class CoachingSessionInfoContainerBody extends React.Component<Props> {
  getCoachFirstName() {
    return this.props.coachingSession?.coachName.split(' ')[0];
  }

  isStudent() {
    return this.props.participantMetadata.studentUuid === this.props.userUuid;
  }

  /**
   * Format like Thu, Nov 19 at 7:00PM (PDT)
   */
  constructNextSessionString() {
    if (
      !this.props.coachingSession?.frequency ||
      !this.props.coachingSession?.nextInstanceStartDateTime
    ) {
      return undefined;
    }
    const nextSession = moment(this.props.coachingSession?.nextInstanceStartDateTime).tz(
      moment.tz.guess()
    );

    return nextSession.format('ddd[,] MMM DD [at] LT [(]z[)]');
  }

  constructNextSessionElement() {
    return (
      <React.Fragment>
        <div>
          <Text weight="semibold" size="medium">
            {this.constructNextSessionString()} •
          </Text>
          <Text
            className="CoachingSessionInfoContainer-duration"
            size="medium">{`${this.props.coachingSession?.duration} min`}</Text>
        </div>
        <Text element="div" size="small">
          with Coach {this.getCoachFirstName()}
        </Text>
      </React.Fragment>
    );
  }

  constuctSessionCardBodyForNextSession() {
    return (
      <MediaObject center figure="right">
        {this.constructNextSessionElement()}
        <MediaObject gutter="small">
          {this.props.messageCoachButton}
          {this.props.joinSessionButton}
        </MediaObject>
      </MediaObject>
    );
  }

  constructSessionCardBodyForNoNextSession() {
    return (
      <MediaObject center figure="right">
        <Text size="small">No upcoming sessions</Text>
        <Button size="xxsmall">Schedule a session</Button>
      </MediaObject>
    );
  }

  constructSessionCardBodyForFreeTrial() {
    const isStudent = this.isStudent();
    const messageCoachTitleCopy = isStudent ? '24 hours in advance' : 'Preparing for your session';
    const messageCoachBodyCopy = isStudent
      ? 'Make sure you sent a sample homework assignment through the message with the coach'
      : 'Make sure your child sends a sample homework assignment through the message with the coach.';

    const joinSessionTitleCopy = isStudent ? '15 minutes in advance' : 'For your session';
    const joinSessionBodyCopy = isStudent
      ? 'Log into your Remind account. We recommend a computer for the video call and a large touch screen device for the whiteboard.'
      : 'Your child will log into their Remind account. We recommend a computer for the video call and a large touch screen device for the whiteboard.';
    const shareHomeworkImage = userAgent.isDesktop()
      ? require('assets/images/coaching/home/share_homework_vert.png')
      : require('assets/images/coaching/home/share_homework_horiz.png');
    const prepareForSessionImage = userAgent.isDesktop()
      ? require('assets/images/coaching/home/prepare_for_session_vert.png')
      : require('assets/images/coaching/home/prepare_for_session_horiz.png');

    return (
      <React.Fragment>
        <Row size="small">{this.constructNextSessionElement()}</Row>
        <Columns2 className="CoachingSessionInfoContainer-cardContainer">
          <CoachingHomeFreeTrialCard
            button={this.props.messageCoachButton}
            titleText={messageCoachTitleCopy}
            bodyText={messageCoachBodyCopy}
            image={shareHomeworkImage}
          />
          <CoachingHomeFreeTrialCard
            button={this.props.joinSessionButton}
            titleText={joinSessionTitleCopy}
            bodyText={joinSessionBodyCopy}
            image={prepareForSessionImage}
          />
        </Columns2>
      </React.Fragment>
    );
  }

  render() {
    switch (this.props.coachingSessionState) {
      case CoachingSessionState.HAS_NO_NEXT_SESSION:
        return this.constructSessionCardBodyForNoNextSession();
      case CoachingSessionState.HAS_NEXT_SESSION:
        return this.constuctSessionCardBodyForNextSession();
      case CoachingSessionState.FREE_TRIAL_WITH_UPCOMING_SESSION:
        return this.constructSessionCardBodyForFreeTrial();
    }
  }
}
